import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Modal } from './Modal'
import { Button } from './Button'

/**
 * Fereastra proprie de confirmare, in locul lui `confirm()` / `prompt()` din browser.
 *
 * Acelea arata altfel in fiecare browser, nu spun ce se intampla dupa clic si au butoane
 * „OK / Cancel" pentru orice — si pentru o stergere, si pentru o trimitere in ExpertAccounts.
 * Aici butonul spune exact actiunea („Sterge factura"), iar ce nu se poate desface e rosu.
 *
 *   const { confirma, cere } = useConfirmare()
 *   if (!(await confirma({ titlu: 'Stergi decontul?', buton: 'Sterge', pericol: true }))) return
 *   const motiv = await cere({ titlu: 'Motivul respingerii', eticheta: 'Motiv' })
 */

interface OptiuniConfirmare {
  titlu: string
  /** Ce se intampla dupa confirmare, in cuvinte. */
  mesaj?: ReactNode
  /** Eticheta butonului — verbul actiunii, nu „OK". */
  buton: string
  /** Actiune care nu se poate desface: buton rosu si semn de atentie. */
  pericol?: boolean
}

interface OptiuniCerere extends Omit<OptiuniConfirmare, 'buton' | 'pericol'> {
  eticheta: string
  buton?: string
  valoareInitiala?: string
  /** Campul nu poate ramane gol. */
  obligatoriu?: boolean
  /** Text pe mai multe randuri. */
  lung?: boolean
}

type Cerere =
  | { tip: 'confirma'; o: OptiuniConfirmare; raspunde: (v: boolean) => void }
  | { tip: 'cere'; o: OptiuniCerere; raspunde: (v: string | null) => void }

const Context = createContext<{
  confirma: (o: OptiuniConfirmare) => Promise<boolean>
  cere: (o: OptiuniCerere) => Promise<string | null>
} | null>(null)

export function ConfirmareProvider({ children }: { children: ReactNode }) {
  const [cerere, setCerere] = useState<Cerere | null>(null)
  const [text, setText] = useState('')
  const campRef = useRef<HTMLInputElement & HTMLTextAreaElement>(null)

  const confirma = useCallback(
    (o: OptiuniConfirmare) =>
      new Promise<boolean>((raspunde) => setCerere({ tip: 'confirma', o, raspunde })),
    [],
  )
  const cere = useCallback(
    (o: OptiuniCerere) =>
      new Promise<string | null>((raspunde) => {
        setText(o.valoareInitiala ?? '')
        setCerere({ tip: 'cere', o, raspunde })
      }),
    [],
  )

  const inchide = (rezultat: boolean) => {
    if (!cerere) return
    if (cerere.tip === 'confirma') cerere.raspunde(rezultat)
    else cerere.raspunde(rezultat ? text.trim() : null)
    setCerere(null)
  }

  const o = cerere?.o
  const pericol = cerere?.tip === 'confirma' && cerere.o.pericol
  const blocat = cerere?.tip === 'cere' && cerere.o.obligatoriu && !text.trim()

  return (
    <Context.Provider value={{ confirma, cere }}>
      {children}
      <Modal
        open={!!cerere}
        onClose={() => inchide(false)}
        title={o?.titlu ?? ''}
        inchideLaClicInAfara={cerere?.tip !== 'cere'}
        footer={
          <>
            <Button variant="secondary" onClick={() => inchide(false)}>
              Renunță
            </Button>
            <Button
              variant={pericol ? 'danger' : 'primary'}
              disabled={blocat}
              onClick={() => inchide(true)}
              autoFocus={cerere?.tip === 'confirma'}
            >
              {cerere?.tip === 'cere' ? (cerere.o.buton ?? 'Salvează') : cerere?.o.buton}
            </Button>
          </>
        }
      >
        <form
          className="grid gap-3 text-corp"
          onSubmit={(e) => {
            e.preventDefault()
            if (!blocat) inchide(true)
          }}
        >
          {o?.mesaj && (
            <div className="flex gap-2.5 text-b4l-text-muted">
              {pericol && <AlertTriangle size={18} className="mt-0.5 shrink-0 text-b4l-red" />}
              <div>{o.mesaj}</div>
            </div>
          )}
          {cerere?.tip === 'cere' && (
            <label className="grid gap-1">
              <span className="text-mic font-medium text-b4l-text-muted">{cerere.o.eticheta}</span>
              {cerere.o.lung ? (
                <textarea
                  ref={campRef}
                  autoFocus
                  rows={4}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className="rounded border border-b4l-border bg-b4l-surface px-3 py-2 outline-none focus:border-b4l-blue"
                />
              ) : (
                <input
                  ref={campRef}
                  autoFocus
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  className="rounded border border-b4l-border bg-b4l-surface px-3 py-2 outline-none focus:border-b4l-blue"
                />
              )}
            </label>
          )}
        </form>
      </Modal>
    </Context.Provider>
  )
}

export function useConfirmare() {
  const c = useContext(Context)
  if (!c) throw new Error('useConfirmare cere <ConfirmareProvider> (pus in main.tsx)')
  return c
}
